import { Font } from 'is24-corecss';
import { format } from 'date-fns';
import { listStyles } from './styles';
import type { CreditHistoryTransaction, CreditHistoryTransactionType } from './types';
import { useIntl } from '@/i18n';

const formatPoints = (points: number) =>
  points > 0 ? `+${points.toLocaleString('de-DE')}` : `${points.toLocaleString('de-DE')}`;
const formatDate = (timestamp: string | number | Date) => format(new Date(timestamp), 'dd.MM.yyyy');

const getEventTypeLabel = (eventType: string, t: ReturnType<typeof useIntl>['t']) => {
  switch (eventType) {
    case 'BENEFIT_UNLOCKED':
      return t({
        id: 'creditsTransactionsLog.eventType.unlockExclusiveContent',
        defaultMessage: 'Unlock Exclusive Content',
      });
    case 'BENEFIT_CREATED':
      return t({
        id: 'creditsTransactionsLog.eventType.subscriptionPurchase',
        defaultMessage: 'Granted credits on subscription purchase',
      });
    case 'MONTHLY_CREDITS_DEDUCTED':
      return t({
        id: 'creditsTransactionsLog.eventType.monthlyDeduction',
        defaultMessage: 'Monthly deduction',
      });
    case 'MONTHLY_CREDITS_ADDED':
      return t({
        id: 'creditsTransactionsLog.eventType.monthlyExtension',
        defaultMessage: 'Monthly extension',
      });
    default:
      return eventType;
  }
};

const getTransactionTypeLabel = (type: CreditHistoryTransactionType, t: ReturnType<typeof useIntl>['t']) =>
  type === 'credit'
    ? t({
        id: 'creditsTransactionsLog.transactionType.credit',
        defaultMessage: 'You have accumulated points',
      })
    : t({
        id: 'creditsTransactionsLog.transactionType.debit',
        defaultMessage: 'You have used points',
      });

type CreditHistoryListItemProps = {
  transaction: CreditHistoryTransaction;
};

export const CreditHistoryListItem = ({ transaction }: CreditHistoryListItemProps) => {
  const { t } = useIntl();

  return (
    <div className={listStyles.row}>
      <Font variant='font-body-medium-regular' className={`${listStyles.date} margin-none`}>
        {formatDate(transaction.timestamp)}
      </Font>
      <span className={listStyles.separator}>|</span>
      <Font variant='font-body-medium-regular' className={`${listStyles.type} margin-none`}>
        {t({
          id: `creditsTransactionsLog.transactionType.${transaction.transactionType}`,
          defaultMessage: getTransactionTypeLabel(transaction.transactionType, t),
        })}
      </Font>
      <Font variant='font-body-medium-bold' className={`${listStyles.description} margin-none`}>
        {getEventTypeLabel(transaction.eventType, t)}
      </Font>
      <Font variant='font-body-medium-bold' className={`${listStyles.points} margin-none`}>
        {formatPoints(transaction.amount)}
      </Font>
    </div>
  );
};
